// objectives-snapshot.ts — the read-model for the in-game OBJECTIVES briefing
// (HTML/DOM UI shell plan, Phase 5, Form 1). Reads the SAME getters the canvas
// ObjectivesForm reads (ui/objectives.ts ObjectivesPlayer / ObjectivesDeps) ONCE
// into a plain object, so the DOM overlay (objectives-view.ts) can render WITHOUT
// touching the sim. The form is read-only (it emits no commands), so the snapshot
// is captured once on open — exactly like the canvas form, which fills its labels
// in Init/UpdateStatistics and never re-reads.
//
// EVERY value here is computed to match the canvas form BYTE-FOR-BYTE:
//   - texts/statuses go through ExpandVars (misccontrols.cpp:1720 — LabelControl
//     SetText) with the SAME getVar the form host uses, so a {pvar}/{$ranktitle}
//     reference expands identically (objectives.ts:117-119 SetText path).
//   - the level title is HtStrupr'd (objectives.ts:113 toUpperCase()).
//   - the elapsed-time string is the verbatim HH:MM:SS divmod off simTickCount
//     (1/100s ticks) from objectives.ts:163-171.
//   - "credits acquired / spent" is `${acquired} / ${consumed}` (objectives.ts:160-161).
//   - the rank title is GetRankTitle (io/stringtable.ts) — the {$ranktitle} the
//     1408 static label expands through the host's getVar (gamesession.ts:709).

import type { ObjectivesDeps } from '../../ui/objectives.ts';
import { ExpandVars, kcbLabelTextMax } from '../../ui/uikit.ts';
import { kcFormalObjectivesMax } from '../../ui/objectives.ts';

/** A single objective row (text on the left, status on the right — kidcObjectiveText/Status). */
export interface ObjectiveRow {
  /** GetFormalObjectiveText(i), {pvar}-expanded; '' for an empty slot. */
  text: string;
  /** GetFormalObjectiveStatus(i) — "COMPLETE" / "INCOMPLETE" / '' — {pvar}-expanded. */
  status: string;
}

/** The 7 STATISTICS rows (forms.ini.pp 1401..1406 + the {$ranktitle} 1408 label). */
export interface ObjectivesStatistics {
  /** GetEnemyMobileUnitsKilled() — "Enemy units destroyed". */
  enemyMobileUnitsKilled: number;
  /** GetEnemyStructuresKilled() — "Enemy buildings destroyed". */
  enemyStructuresKilled: number;
  /** GetMobileUnitsLost() — "Friendly units lost". */
  mobileUnitsLost: number;
  /** GetStructuresLost() — "Friendly buildings lost". */
  structuresLost: number;
  /** `${GetTotalCreditsAcquired()} / ${GetTotalCreditsConsumed()}` — "Credits acquired / spent". */
  creditsAcquiredSpent: string;
  /** the simTickCount() (1/100s) rendered HH:MM:SS — "Elapsed time". */
  elapsedTime: string;
  /** GetRankTitle({$ranktitle}) — the "Title" row. */
  rankTitle: string;
}

/** The whole OBJECTIVES briefing read-model — captured once when the form opens. */
export interface ObjectivesSnapshot {
  /** getLevelTitle() upper-cased (HtStrupr; objectives.ts:113). */
  missionTitle: string;
  /** the 4 objective rows (text + status). */
  objectives: ObjectiveRow[];
  /** GetFormalObjectiveInfo(), {pvar}-expanded — the BRIEFING multiline body. */
  briefing: string;
  /** the 7 STATISTICS rows. */
  statistics: ObjectivesStatistics;
}

// objectives.ts:163-171 — UpdateStatistics' GameTime label: simTickCount (1/100s)
// divmod into HH:MM:SS, each field "%02d". The C++ Math.trunc (integer divide)
// is preserved; negative/huge inputs are simply formatted as the C++ would.
export function formatElapsedTime(simTickCount: number): string {
  let t = simTickCount;
  const nHour = Math.trunc(t / (100 * 60 * 60));
  t -= nHour * (100 * 60 * 60);
  const nMin = Math.trunc(t / (100 * 60));
  t -= nMin * (100 * 60);
  const nSec = Math.trunc(t / 100);
  const pad = (n: number): string => String(n).padStart(2, '0'); // sprintf %02d
  return `${pad(nHour)}:${pad(nMin)}:${pad(nSec)}`;
}

/** Build the read-model from the SAME deps the canvas ShowObjectives reads.
 *  `getVar` is the form host's expander (gamesession.ts makeFormMgrHost getVar) —
 *  the DOM form is NOT mounted on a FormMgr, so it can't lean on LabelControl's
 *  ExpandVars-in-SetText; we apply the SAME expansion here for exact parity. */
export function objectivesSnapshot(
  deps: ObjectivesDeps,
  getVar: (name: string) => string,
): ObjectivesSnapshot {
  const plr = deps.plr;
  const expand = (s: string | null): string =>
    s === null ? '' : ExpandVars(s, kcbLabelTextMax, getVar);

  const objectives: ObjectiveRow[] = [];
  for (let i = 0; i < kcFormalObjectivesMax; i++) {
    objectives.push({
      text: expand(plr.GetFormalObjectiveText(i)),
      status: expand(plr.GetFormalObjectiveStatus(i)),
    });
  }

  return {
    // HtStrupr (objectives.ts:113) — the title control is the upper-cased level title.
    missionTitle: deps.getLevelTitle().toUpperCase(),
    objectives,
    briefing: expand(plr.GetFormalObjectiveInfo()),
    statistics: {
      enemyMobileUnitsKilled: plr.GetEnemyMobileUnitsKilled(),
      enemyStructuresKilled: plr.GetEnemyStructuresKilled(),
      mobileUnitsLost: plr.GetMobileUnitsLost(),
      structuresLost: plr.GetStructuresLost(),
      // objectives.ts:160-161 — `${acquired} / ${consumed}`
      creditsAcquiredSpent: `${plr.GetTotalCreditsAcquired()} / ${plr.GetTotalCreditsConsumed()}`,
      elapsedTime: formatElapsedTime(deps.simTickCount()),
      // the {$ranktitle} the 1408 label expands through the host's getVar.
      rankTitle: expand('{$ranktitle}'),
    },
  };
}
